import { apiClient, getPlatformUrl } from "../../lib/api";
import { IAugmentationRepo } from "./irepo";
import {
  DataGenerationRequest,
  DataGenerationResponse,
  DataGenerationTask,
  SourceDatasetSize,
  SourceDatasetVariant,
  GeneratedDatasetListResponse,
  GeneratedDatasetDetail,
  HistogramRequest,
  HistogramResponse,
  DataGenerationEstimateRequest,
  PriceEstimateResponse,
  CostLogsResponse,
  CostDateFilterBy,
} from "./type/augmentation-def";

const ROOT = () => `${getPlatformUrl()}/api/message-queue/data-generation`;

export class AugmentationGateway implements IAugmentationRepo {
  async estimatePrice(
    req: DataGenerationEstimateRequest
  ): Promise<PriceEstimateResponse> {
    const url = `${ROOT()}/estimate`;
    return await apiClient.post({ url, body: req, useToken: true }) as PriceEstimateResponse;
  }

  async getCostLogs(
    clientId: string,
    startDateUtc: string,
    endDateUtc: string,
    dateFilterBy: CostDateFilterBy = "finished_at"
  ): Promise<CostLogsResponse> {
    const params = new URLSearchParams({
      client_id: clientId,
      start_date_utc: startDateUtc,
      end_date_utc: endDateUtc,
      date_filter_by: dateFilterBy,
    });
    const url = `${ROOT()}/billing/cost-logs?${params.toString()}`;
    return await apiClient.get({ url, useToken: true }) as CostLogsResponse;
  }

  async getSourceSize(
    modelId: string,
    sourceDatasetVariant: SourceDatasetVariant = "nondiscretized"
  ): Promise<SourceDatasetSize> {
    const url = `${ROOT()}/models/${modelId}/source-dataset/size?source_dataset_variant=${sourceDatasetVariant}`;
    return await apiClient.get({ url, useToken: true }) as SourceDatasetSize;
  }

  async createTask(req: DataGenerationRequest): Promise<DataGenerationResponse> {
    const url = `${ROOT()}/tasks`;
    return await apiClient.post({ url, body: req, useToken: true }) as DataGenerationResponse;
  }

  async getTask(taskId: string): Promise<DataGenerationTask> {
    const url = `${ROOT()}/tasks/${taskId}`;
    return await apiClient.get({ url, useToken: true }) as DataGenerationTask;
  }

  async listGeneratedDatasets(
    modelId: string,
    limit = 50,
    cursor?: string
  ): Promise<GeneratedDatasetListResponse> {
    let url = `${ROOT()}/models/${modelId}/generated-datasets?limit=${limit}`;
    if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
    return await apiClient.get({ url, useToken: true }) as GeneratedDatasetListResponse;
  }

  async getGeneratedDataset(generatedDatasetId: string): Promise<GeneratedDatasetDetail> {
    const url = `${ROOT()}/generated-datasets/${generatedDatasetId}`;
    return await apiClient.get({ url, useToken: true }) as GeneratedDatasetDetail;
  }

  async getHistograms(
    generatedDatasetId: string,
    req: HistogramRequest
  ): Promise<HistogramResponse> {
    const url = `${ROOT()}/generated-datasets/${generatedDatasetId}/histograms`;
    return await apiClient.post({ url, body: req, useToken: true }) as HistogramResponse;
  }

  async downloadDataset(generatedDatasetId: string): Promise<string> {
    const url = `${ROOT()}/generated-datasets/${generatedDatasetId}/download`;
    return await apiClient.get({ url, useToken: true, responseType: "text" }) as string;
  }
}
